<template>
  <hs-modal v-model="open" title="Edit Promo Code" size="sm" @close="$emit('close')">
    <div class="egpcm-body">
      <div class="egpcm-code-card">
        <div class="egpcm-code-card__top">
          <span class="egpcm-code-card__code">{{ promoCode.code }}</span>
          <hs-badge :tone="getStatusTone(promoCode)">{{ getStatus(promoCode) }}</hs-badge>
        </div>
        <p class="egpcm-code-card__discount">{{ formatDiscount(promoCode) }} off</p>
      </div>

      <hs-date-input
        v-model="formData.expiration_date"
        type="datetime-local"
        label="Expiration Date"
        placeholder="Select expiration date"
      />

      <hs-input
        v-model.number="formData.usage_limit"
        label="Usage Limit"
        type="number"
        min="0"
        placeholder="0 for unlimited"
        :description="`Current usage: ${promoCode.usage_count || 0}`"
      />
    </div>

    <template #footer>
      <div class="egpcm-footer">
        <hs-button variant="ghost" type="button" @click="$emit('close')">Cancel</hs-button>
        <hs-button
          variant="primary"
          icon="save"
          :loading="isLoading"
          :disabled="!isValid"
          @click="handleSubmit"
        >
          Update Promo Code
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import HsDateInput from "@/components/ui/HsDateInput.vue";
import { HsModal, HsButton, HsInput, HsBadge } from "@/components/ui";

export default {
  name: "EditGroupPromoCodeModal",
  components: { HsModal, HsButton, HsInput, HsBadge, HsDateInput },
  props: {
    groupId: { type: Number, required: true },
    promoCode: { type: Object, required: true },
  },
  emits: ["close", "updated"],
  setup(props, { emit }) {
    const open = ref(true);
    const isLoading = ref(false);

    const formData = ref({ expiration_date: "", usage_limit: null });

    const formatDateForInput = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const formatDateForApi = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    watch(() => props.promoCode, (newCode) => {
      if (newCode) {
        formData.value.expiration_date = formatDateForInput(newCode.expiration_date);
        formData.value.usage_limit = newCode.usage_limit || 0;
      }
    }, { immediate: true });

    const isValid = computed(() => formData.value.expiration_date && formData.value.usage_limit >= 0);

    const handleSubmit = async () => {
      if (!isValid.value) return;
      isLoading.value = true;
      try {
        await http.patch(`/v2/groups/${props.groupId}/promo-codes/${props.promoCode.id}`, {
          expiration_date: formatDateForApi(formData.value.expiration_date),
          usage_limit: formData.value.usage_limit,
        });
        toast.success("Promo code updated successfully");
        emit("updated");
        emit("close");
      } catch (err) {
        console.error("Failed to update promo code", err);
        toast.error(err?.response?.data?.error?.message || "Unable to update promo code.");
      } finally {
        isLoading.value = false;
      }
    };

    const getStatus = (code) => {
      if (!code.is_active) return "Inactive";
      if (code.expiration_date && new Date(code.expiration_date) < new Date()) return "Expired";
      if (code.usage_limit && (code.usage_count || 0) >= code.usage_limit) return "Depleted";
      return "Active";
    };

    const getStatusTone = (code) => {
      const tones = { Active: "success", Inactive: "neutral", Expired: "danger", Depleted: "warning" };
      return tones[getStatus(code)] || "neutral";
    };

    const formatDiscount = (code) => {
      if (code.discount_percentage) return `${parseFloat(code.discount_percentage)}%`;
      if (code.discount_amount) return `$${parseFloat(code.discount_amount).toFixed(2)}`;
      return "N/A";
    };

    return { open, isLoading, formData, isValid, handleSubmit, getStatus, getStatusTone, formatDiscount };
  },
};
</script>

<style scoped>
.egpcm-body {
  display: grid;
  gap: 1rem;
}

.egpcm-code-card {
  background: color-mix(in srgb, var(--hs-bg-soft) 72%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  display: grid;
  gap: 0.3rem;
  padding: 0.9rem 1rem;
}

.egpcm-code-card__top {
  align-items: center;
  display: flex;
  gap: 0.6rem;
  justify-content: space-between;
}

.egpcm-code-card__code {
  color: var(--hs-text);
  font-family: monospace;
  font-size: 1.1rem;
  font-weight: 850;
  letter-spacing: 0.06em;
}

.egpcm-code-card__discount {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin: 0;
}

.egpcm-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
